import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { clientsAPI, sitesAPI } from '../utils/api';
import { Users, Building2, Mail, Phone, MapPin, Plus, Edit, Archive, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DemoModeToggle } from '../components/DemoModeToggle';
import { CreateSiteModal } from '../components/CreateSiteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientsManagementPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [archivingClient, setArchivingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showCreateSite, setShowCreateSite] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, sitesRes] = await Promise.all([
        clientsAPI.getAll(),
        sitesAPI.getAll(),
      ]);
      setClients(clientsRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      contact_person: client.contact_person,
      contact_email: client.contact_email,
      contact_phone: client.contact_phone,
      address: client.address,
    });
    setShowEditClient(true);
  };

  const handleArchiveClick = (client) => {
    setArchivingClient(client);
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingClient) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/clients/${archivingClient.client_id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Client archived successfully');
      setShowArchiveConfirm(false);
      setArchivingClient(null);
      fetchData();
    } catch (error) {
      console.error('Failed to archive client:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingClient) {
        // Update existing client
        const token = localStorage.getItem('token');
        await axios.put(
          `${BACKEND_URL}/api/clients/${editingClient.client_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Client updated successfully!');
        setShowEditClient(false);
      } else {
        // Create new client
        await clientsAPI.create(formData);
        toast.success('Client created successfully!');
        setShowAddClient(false);
      }
      
      setFormData({
        name: '',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        address: '',
      });
      setEditingClient(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSubmitting(false);
    }
  };

  const getClientSites = (clientId) => {
    return sites.filter((s) => s.client_id === clientId);
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader
            title="Client Management"
            description="Add, edit, and manage your BESS client accounts"
            action={
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => setShowCreateSite(true)}
                  variant="outline"
                  data-testid="create-site-from-clients"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Create Site
                </Button>
                <Button
                  onClick={() => setShowAddClient(true)}
                  data-testid="add-client-button"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>
            }
          />

          <div className="flex justify-end mb-4">
            <DemoModeToggle onToggle={fetchData} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {clients.map((client) => {
              const clientSites = getClientSites(client.client_id);
              return (
                <div
                  key={client.client_id}
                  data-testid={`client-card-${client.client_id}`}
                  className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3
                          className="font-semibold text-slate-900 text-lg cursor-pointer hover:text-blue-700 transition-colors"
                          onClick={() => navigate(`/clients/${client.client_id}`)}
                          data-testid={`client-name-${client.client_id}`}
                        >
                          {client.name}
                        </h3>
                        <p className="text-sm text-slate-600">{clientSites.length} sites</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(client)}
                        data-testid={`edit-client-${client.client_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleArchiveClick(client)}
                        data-testid={`archive-client-${client.client_id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm text-slate-600">
                      <Users className="w-4 h-4 mr-2 text-slate-400" />
                      {client.contact_person}
                    </div>
                    <div className="flex items-center text-sm text-slate-600">
                      <Mail className="w-4 h-4 mr-2 text-slate-400" />
                      {client.contact_email}
                    </div>
                    <div className="flex items-center text-sm text-slate-600">
                      <Phone className="w-4 h-4 mr-2 text-slate-400" />
                      {client.contact_phone}
                    </div>
                    <div className="flex items-start text-sm text-slate-600">
                      <MapPin className="w-4 h-4 mr-2 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span>{client.address}</span>
                    </div>
                  </div>

                  {clientSites.length > 0 && (
                    <div className="pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Sites:</p>
                      <div className="flex flex-wrap gap-2">
                        {clientSites.map((site) => (
                          <span
                            key={site.site_id}
                            onClick={() => navigate(`/sites/${site.site_id}`)}
                            className="inline-flex items-center px-2 py-1 bg-slate-50 text-slate-700 rounded text-xs cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            data-testid={`site-link-${site.site_id}`}
                          >
                            <Building2 className="w-3 h-3 mr-1" />
                            {site.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-4 mt-4 border-t border-slate-100 flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/clients/${client.client_id}/billing`)}
                      data-testid={`view-billing-${client.client_id}`}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      View Billing
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add/Edit Client Dialog */}
      <Dialog open={showAddClient || showEditClient} onOpenChange={(open) => {
        if (!open) {
          setShowAddClient(false);
          setShowEditClient(false);
          setEditingClient(null);
          setFormData({
            name: '',
            contact_person: '',
            contact_email: '',
            contact_phone: '',
            address: '',
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., GreenTech Industries"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact_person">Contact Person *</Label>
                <Input
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder="e.g., John Doe"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact_email">Contact Email *</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  placeholder="e.g., john@company.com"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact_phone">Contact Phone *</Label>
                <Input
                  id="contact_phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  placeholder="e.g., +91-9876543210"
                  required
                  className="mt-2"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="e.g., Plot 42, Industrial Area, City, State 560001"
                  required
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddClient(false);
                  setShowEditClient(false);
                  setEditingClient(null);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {submitting ? (editingClient ? 'Updating...' : 'Creating...') : (editingClient ? 'Update Client' : 'Create Client')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span>Archive Client?</span>
            </DialogTitle>
            <DialogDescription className="pt-4">
              Are you sure you want to archive <strong>{archivingClient?.name}</strong>?
              <br /><br />
              <span className="text-amber-600">⚠️ This will hide the client from the main list but preserve all data.</span>
              <br />
              {getClientSites(archivingClient?.client_id).length > 0 && (
                <span className="text-red-600">
                  <br />
                  This client has {getClientSites(archivingClient?.client_id).length} active site(s). 
                  Consider archiving those first.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowArchiveConfirm(false);
                setArchivingClient(null);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveConfirm}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Archiving...' : 'Archive Client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreateSiteModal
        open={showCreateSite}
        onClose={() => setShowCreateSite(false)}
        onCreated={() => fetchData()}
      />
    </div>
  );
};
